/**
 * QorTrace — Language switcher (Iter249)
 *
 * Dropdown selector for switching between supported locales. Designed to
 * sit in the footer or top nav. Persists the choice via i18next's cookie +
 * localStorage stack and reloads any `useTranslation()` consumers.
 */
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import { SUPPORTED_LANGUAGES, SUPPORTED_LANGUAGE_CODES } from "../i18n";

export default function LanguageSwitcher({ compact = false, align = "left" }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const current = SUPPORTED_LANGUAGES.find(
    (l) => l.code === (i18n.resolvedLanguage || i18n.language || "en").split("-")[0]
  ) || SUPPORTED_LANGUAGES[0];

  const pick = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
    // Update <html lang> for SEO + a11y screen readers
    try {
      document.documentElement.setAttribute("lang", code);
    } catch (_) { /* no-op */ }

    // Iter250 — Rewrite the URL so the locale prefix matches the chosen
    // language. This keeps SEO clean (each locale has its own canonical
    // URL) and lets users share locale-stamped links.
    //
    // - EN: strip any `/de/`, `/fr/`, `/es/`, `/ja/` prefix.
    // - Non-EN: ensure the prefix is `/<code>/`.
    try {
      const { pathname, search, hash } = window.location;
      const segments = pathname.split("/").filter(Boolean);
      const head = (segments[0] || "").toLowerCase();
      const tail = SUPPORTED_LANGUAGE_CODES.includes(head)
        ? "/" + segments.slice(1).join("/")
        : pathname;
      const cleanTail = tail === "/" || tail === "" ? "/" : tail.startsWith("/") ? tail : `/${tail}`;
      const nextPath = code === "en" ? cleanTail : `/${code}${cleanTail === "/" ? "" : cleanTail}`;
      // Use a full reload so BrowserRouter picks up the new basename.
      window.location.assign(nextPath + search + hash);
    } catch (_) { /* no-op */ }
  };

  const alignClass = align === "right" ? "right-0" : "left-0";

  return (
    <div className="relative inline-block" ref={ref} data-testid="lang-switcher-root">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 ${compact ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm"} rounded-md border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-white transition`}
        data-testid="lang-switcher-trigger"
        aria-label="Choose language"
        aria-expanded={open}
      >
        <Globe className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
        <span className="font-mono uppercase text-[10px] tracking-wider">{current.code}</span>
        {!compact && (
          <span className="text-zinc-400 text-xs ml-1">{current.native}</span>
        )}
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>

      {open && (
        <div
          className={`absolute ${alignClass} mt-1 z-50 min-w-[180px] rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl py-1`}
          data-testid="lang-switcher-menu"
          role="listbox"
        >
          {SUPPORTED_LANGUAGES.map((lang) => {
            const active = lang.code === current.code;
            return (
              <button
                key={lang.code}
                onClick={() => pick(lang.code)}
                className={`w-full px-3 py-2 text-left flex items-center justify-between gap-3 text-sm transition ${active ? "bg-zinc-900 text-white" : "text-zinc-300 hover:bg-zinc-900 hover:text-white"}`}
                role="option"
                aria-selected={active}
                data-testid={`lang-option-${lang.code}`}
              >
                <span className="flex items-center gap-2">
                  <span className="text-base leading-none">{lang.flag}</span>
                  <span className="leading-tight">
                    <div>{lang.native}</div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{lang.name}</div>
                  </span>
                </span>
                {active && <Check className="w-3.5 h-3.5 text-cyan-400" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
